/// <reference types="node"/>
/**
 * Authenticate users via Contentful OAuth login
 *
 * https://www.contentful.com/developers/docs/extensibility/oauth/
 * https://www.contentful.com/developers/docs/references/authentication/
 *
 * * OAuth2 via Contentful requires an Contentful OAuth2 app being set up with a static redirect URL
 *
 * Authentication process
 * ======================
 *
 * 1. Routes that require authentication use the authenticationGuard() navigation guard exported by this module
 * 2. if the user is not logged in when trying to access such route he is redirected to the login route,
 *    with the previous route path stored passed to the login view as prop `redirectPath`
 * 3. the login view links to the auth.init-oauth route
 * 4. The auth.init-oauth route either
 * 	  a) redirects to the Contentful OAuth2 login, appending `redirectPath` to the params of the callback URL (continue step 5) or
 *    b) directly authenticates the user if a management accessToken is provided via process.env.CONTENTFUL_MANAGEMENT_TOKEN
 *       and redirects the user to `redirectPath`
 * 5. After a successful login at CF, CF forwards the user to a generic callback route on our end
 *    (passed in via param as well as configured in the ContentFul OAuth2 app settings)
 * 6. The callback URL extracts and stores the accessToken provided and redirects the user to `redirectPath`
 */

import AuthStoreModule from '@app/store/modules/AuthStoreModule';
import CacheError, { CacheErrorType } from '@app/types/errors/CacheError';
import { Route, RouteConfig } from 'vue-router';
import { getModule as getStoreModule } from 'vuex-module-decorators';

const authStoreModule = getStoreModule(AuthStoreModule);

// Store the next handler for the authentication process so we may redirect after auth success
let authNext: ((route?: any) => void) | undefined;

/**
 * In other bits of the application we're throwing CacheErrors instead of ignoring them, but check if caching is supported
 * via AppStoreModule first before invoking saveState()/restoreState().
 * In navigation guards, AppStoreModule isn't initialized yet, so we're invoking saveState() nonetheless and
 * provide a warning if auth data could not be persisted
 *
 * @param error
 */
function handleCacheError(error: Error): void {
	if (error instanceof CacheError && (error as CacheError).type === CacheErrorType.CachingUnsupportedInClientError) {
		console.warn('[auth routes] Could not persist auth state in cache - client does not support caching');
		return;
	}
	throw error;
}

// Store the opener reference if we are a popup window
const isPopupWindow = !!window.opener && window.opener !== window;

export const authenticationRoutes: RouteConfig[] = [
	// callback from Contentful after successful login
	{
		path: '/oauth/callback',
		beforeEnter: (to: Route, _from: Route, next: (route?: any) => void): void => {
			const hashedParameters = Object.fromEntries(new URLSearchParams(to.hash.substring(1)));
			const accessToken = hashedParameters.access_token as string;

			// Different handling based on whether we're in a popup or main window
			if (isPopupWindow) {
				// We are in a popup - send a message to the opener
				console.log('[auth routes] Running in popup, sending message to opener');

				let openerOrigin = '';

				// Check if this window was opened as a popup
				try {
					// Determine the origin of the opener window
					openerOrigin = localStorage.getItem('opener_origin') || '*';
				} catch (e) {
					console.warn('[auth routes] Error checking window opener:', e);
				}

				try {
					// Send the access token to the opener (iframe or main window)
					window.opener.postMessage(
						{
							type: 'CONTENTFUL_AUTH_SUCCESS',
							accessToken,
							redirectPath: to.query.redirectPath
						},
						openerOrigin
					);

					// Show a success message to user
					document.body.innerHTML = `
						<div style="text-align: center; padding: 20px; font-family: sans-serif;">
							<h2>Authentication Successful</h2>
							<p>You can close this window and return to the application.</p>
						</div>
					`;

					// Don't navigate anywhere - this window will be closed by the opener
					next(false);
				} catch (error) {
					console.error('[auth routes] Error sending message to opener:', error);
					// If we can't communicate with the opener, try to at least show an error
					document.body.innerHTML = `
						<div style="text-align: center; padding: 20px; font-family: sans-serif;">
							<h2>Authentication Complete</h2>
							<p>There was a problem communicating with the main application.</p>
							<p>Please close this window and try again.</p>
						</div>
					`;
					next(false);
				}
			} else {
				// We are in the main window or iframe - handle normally
				authStoreModule.login(accessToken);

				// @see handleCacheError()
				authStoreModule.saveState().catch(handleCacheError);

				next(String(to.query.redirectPath));
			}
		}
	},

	// linked to whenever a login should be initiated (user will not be asked but just forwared to login / logged in)
	// pass in query param redirectPath with the target url once authentication is complete
	{
		path: '/oauth/init',
		name: 'auth.init-oauth',
		beforeEnter: (to: Route, _from: Route, next: (route?: any) => void): void => {
			const managementToken: string = authStoreModule.managementToken || process.env.CONTENTFUL_MANAGEMENT_TOKEN || '';

			// eslint-disable-next-line no-undefined
			if (![undefined, '', '-'].includes(managementToken)) {
				// there's a fixed management token defined for this environment, don't use oauth process
				authStoreModule.login(managementToken);

				// @see handleCacheError()
				authStoreModule.saveState().catch(handleCacheError);

				next(String(to.query.redirectPath));
				return;
			}

			// make sure the user will be redirected back to the previous route
			const callbackUrl = `${window.location.protocol}//${window.location.host}${process.env.PUBLIC_PATH}oauth/callback?redirectPath=${to.query.redirectPath}`;
			const authorizeUrl = String(process.env.OAUTH_AUTHORIZE_URL).replace('{callbackUrl}', callbackUrl);

			// Check if we're in an iframe
			const isInIframe = window !== window.parent;

			if (isInIframe) {
				// We're in an iframe (likely CF live preview).
				// We can't redirect to Contentful auth pages as this is forbidden Content policies set by Contentful.
				// So we're using a popup window here to handle Contentful authentication.

				// Set up a message listener before opening the popup to handle auth responses:
				setupAuthMessageListener();

				// Store the next handler so we may invoke it in the auth message listener
				authNext = next;

				// Store the current origin in localStorage so the popup can read it
				try {
					localStorage.setItem('opener_origin', window.location.origin);
				} catch (e) {
					console.warn('[auth routes] Could not store opener origin in localStorage:', e);
				}

				// Open the auth URL in a popup
				const authWindow = window.open(authorizeUrl, 'contentful-login', 'width=800,height=600');

				// If popup was blocked, show an error message
				if (!authWindow) {
					console.error('[auth routes] Popup was blocked. Cannot authenticate.');
					// You might want to show a user-friendly message here
					next(`/popup-blocked`);
					return;
				}

				// We don't navigate anywhere - we wait for the message from the popup
				next(false);
			} else {
				// Not in an iframe, proceed with normal navigation
				window.location.href = authorizeUrl;
				next(false);
			}
		}
	},

	// linked to whenever the user should be asked for a login
	// pass in query param redirectPath with the target url once authentication is complete
	{
		path: '/login',
		name: 'auth.login',
		redirect: { name: 'auth.init-oauth' }
	},

	// linked to whenever the user should be logged out
	{
		path: '/logout',
		name: 'auth.logout',
		beforeEnter: (to: Route, _from: Route, next: (route?: any) => void): void => {
			authStoreModule.logout();

			// @see handleCacheError()
			authStoreModule.saveState().catch(handleCacheError);

			if (process.env.CONTENTFUL_LOGOUT_URL as string) {
				// Check if we're in an iframe
				if (window !== window.parent) {
					// We're in an iframe - open logout in a popup
					const logoutWindow = window.open(
						process.env.CONTENTFUL_LOGOUT_URL as string,
						'contentful-logout',
						'width=800,height=600'
					);

					if (!logoutWindow) {
						console.error('[auth routes] Logout popup was blocked.');
					} else {
						// Close the logout window after a delay
						setTimeout(() => {
							if (logoutWindow && !logoutWindow.closed) {
								logoutWindow.close();
							}
						}, 3000);
					}

					// Continue with local navigation
					next(to.query.redirectPath || '/');
				} else {
					// Not in an iframe, proceed with normal navigation
					window.location.href = process.env.CONTENTFUL_LOGOUT_URL as string;
				}
			} else {
				next(to.query.redirectPath || '/');
			}
		}
	},

	// Fallback route for when popups are blocked
	{
		path: '/popup-blocked',
		name: 'auth.popup-blocked',
		component: {
			template: `
				<div style="text-align: center; padding: 20px;">
					<h2>Popup Blocked</h2>
					<p>Please enable popups for this site to continue with authentication.</p>
					<button @click="retryAuth">Try Again</button>
				</div>
			`,
			methods: {
				retryAuth() {
					const redirectPath = this.$route.query.redirectPath || '/';
					this.$router.push({
						name: 'auth.init-oauth',
						query: { redirectPath }
					});
				}
			}
		}
	}
];

/**
 * Sets up a message listener for authentication messages from popup window
 */
function setupAuthMessageListener(): void {
	// Clean up any existing listeners first
	window.removeEventListener('message', handleAuthMessage);

	// Add the message listener
	window.addEventListener('message', handleAuthMessage);

	// Set a timeout to clean up the listener if authentication doesn't complete
	setTimeout(() => {
		window.removeEventListener('message', handleAuthMessage);
	}, 300000); // 5 minutes timeout
}

/**
 * Handles authentication messages from the popup window
 */
async function handleAuthMessage(event: MessageEvent): Promise<void> {
	// Verify the message format
	if (!event.data || typeof event.data !== 'object' || !event.data.type) {
		return;
	}

	window.removeEventListener('message', handleAuthMessage);

	// Handle successful authentication
	if (event.data.type === 'CONTENTFUL_AUTH_SUCCESS') {
		console.log('[auth routes] Received authentication success message');

		// Get the token from the message
		const { accessToken, redirectPath } = event.data;

		if (accessToken) {
			// Login using the token
			await authStoreModule.login(accessToken);

			// Save the auth state
			await authStoreModule.saveState().catch(handleCacheError);

			console.log('[auth routes] Login successful');

			// Close the popup if we can
			if (event.source && (event.source as Window).close) {
				try {
					(event.source as Window).close();
				} catch (e) {
					console.warn('[auth routes] Could not close popup:', e);
				}
			}

			if (authNext) {
				authNext(redirectPath);
				authNext = undefined; // Clear reference after use
			} else {
				window.location.href = redirectPath;
			}
		}
	}
}

/**
 * Ensures the user is authenticated against contentful before accessing the guarded route
 * @param to
 * @param _from
 * @param next
 */
export const authenticationGuard = (to: Route, _from: Route, next: (route?: any) => void): void => {
	console.log('[auth routes] authentication guard triggered', to, authStoreModule.managementToken);
	// see if the user is already logged in. in this case there's no need to guard this route
	if (authStoreModule.managementToken) {
		next();
		return;
	}

	next({
		name: 'auth.login',
		query: {
			redirectPath: to.fullPath
		}
	});
};
